import { useState } from 'react';
import { useQueryClient } from 'react-query';

import { accountStatusKey } from '@identity/presentation/constants';
import { useIdentityUserId } from '@identity/presentation/hooks';

import { EmailAwaitingView } from './EmailAwaitingView';
import { EmailCaptureView } from './EmailCaptureView';

export type EmailStepProps = {
  /** The address the account has today. It preloads the capture field and is what the exit returns to. */
  accountEmail: string;
  /** Already translated. Absent means this flow does not number its steps. */
  stepLabel?: string;
};

/**
 * The email step: one component with two views.
 *
 * Waiting is not a separate step. The address is not confirmed until the link is opened, so the
 * backend still reports the requirement as pending and the derived step does not move. The only
 * thing this component stores is whether a link was sent on this screen.
 *
 * That has an intended effect: reloading returns to the capture view with the account address back
 * in the field, because nothing was saved and abandoning here changes nothing.
 *
 * @param {EmailStepProps} props - Component properties
 */
export const EmailStep = ({ accountEmail, stepLabel }: EmailStepProps) => {
  const queryClient = useQueryClient();
  const userId = useIdentityUserId();
  const [sentTo, setSentTo] = useState<string | null>(null);

  // The polling never writes to the cache: this refetch is what moves the wizard on.
  const onVerified = () => {
    queryClient.invalidateQueries(accountStatusKey(userId));
  };

  if (!sentTo) return <EmailCaptureView accountEmail={accountEmail} onSent={setSentTo} stepLabel={stepLabel} />;

  return <EmailAwaitingView email={sentTo} onUseAnotherEmail={() => setSentTo(null)} onVerified={onVerified} />;
};
